// components/CanvasDrawingToolOverlay.tsx
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type { DrawingToolKind, DrawingToolStyle, Layer } from '#types/types';
import {
  createPencilDrawingSvg,
  createShapeDrawingSvg,
  getDrawingLayerPayload,
  normalizeDrawingStyle,
  type DrawingSvgResult,
} from '#lib/drawing-layer';
import { svgMarkupToDataUrl } from '#lib/svg-layer';

export type ShapeDrawingKind =
  | 'rectangle'
  | 'ellipse'
  | 'triangle'
  | 'diamond'
  | 'star'
  | 'pentagon'
  | 'hexagon'
  | 'line'
  | 'arrow';

export type ShapeDrawingDraft = {
  workspaceId: string;
  start: { x: number; y: number };
  current: { x: number; y: number };
  kind: ShapeDrawingKind;
};

export type PencilDrawingDraft = {
  workspaceId: string;
  points: Array<{ x: number; y: number }>;
  dragStart: { x: number; y: number };
  isDragging: boolean;
  hasDragMoved: boolean;
};

// Free-polygon draft: vertices are placed by discrete clicks (world coordinates), `cursor` follows
// the pointer for the rubber-band line, and `canClose` flags when the cursor is within snap range of
// the first vertex (with >= 3 points) so the overlay can highlight the closing anchor.
export type PolygonDrawingDraft = {
  workspaceId: string;
  points: Array<{ x: number; y: number }>;
  cursor: { x: number; y: number } | null;
  canClose: boolean;
};

export type TextDrawingDraft = {
  workspaceId: string;
  anchor: { x: number; y: number };
  value: string;
  editingLayerId?: string;
  style: DrawingToolStyle;
};

export const isShapeDrawingKind = (kind: DrawingToolKind): kind is ShapeDrawingKind => (
  kind === 'rectangle' ||
  kind === 'ellipse' ||
  kind === 'triangle' ||
  kind === 'diamond' ||
  kind === 'star' ||
  kind === 'pentagon' ||
  kind === 'hexagon' ||
  kind === 'line' ||
  kind === 'arrow'
);

export const isDrawingControlsTarget = (target: EventTarget | null): boolean => (
  target instanceof Element && target.closest('[data-drawing-controls="true"]') !== null
);

export const resolveDisplayedTextDrawingStyle = (
  layer: Layer,
  payload: NonNullable<ReturnType<typeof getDrawingLayerPayload>>,
): DrawingToolStyle => {
  const imageWidth = layer.imageData?.width ?? payload.worldRect.w;
  const imageHeight = layer.imageData?.height ?? payload.worldRect.h;
  const scaleCandidates = [
    imageWidth > 0 && layer.rect.w > 0 ? layer.rect.w / imageWidth : null,
    imageHeight > 0 && layer.rect.h > 0 ? layer.rect.h / imageHeight : null,
  ].filter((value): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0);
  const rectScale = scaleCandidates.length > 0
    ? scaleCandidates.reduce((sum, value) => sum + value, 0) / scaleCandidates.length
    : null;
  const layerScale =
    typeof layer.scale === 'number' && Number.isFinite(layer.scale) && layer.scale > 0
      ? layer.scale
      : null;
  const scale = rectScale ?? layerScale ?? 1;
  return {
    ...payload.style,
    fontSize: Math.max(8, Math.min(1024, Math.round(payload.style.fontSize * scale))),
  };
};

const DrawingPreviewSvg = React.memo(({
  result,
}: {
  result: DrawingSvgResult;
}) => {
  return (
    <img
      src={svgMarkupToDataUrl(result.svg)}
      alt=""
      draggable={false}
      style={{
        position: 'absolute',
        left: result.worldRect.x,
        top: result.worldRect.y,
        width: result.worldRect.w,
        height: result.worldRect.h,
        pointerEvents: 'none',
        zIndex: 30,
      }}
    />
  );
});

DrawingPreviewSvg.displayName = 'DrawingPreviewSvg';

// Live preview for an in-progress free polygon: placed edges + rubber-band line to the cursor, a dot
// at each vertex, and a highlighted ring on the first vertex when the cursor is within close range.
const PolygonDrawingPreview = React.memo(({
  draft,
  drawingStyle,
}: {
  draft: PolygonDrawingDraft;
  drawingStyle: DrawingToolStyle;
}) => {
  const style = normalizeDrawingStyle(drawingStyle);
  const points = draft.points;
  if (points.length === 0) return null;

  const cursor = draft.cursor;
  const allPoints = cursor ? [...points, cursor] : points;
  const xs = allPoints.map((point) => point.x);
  const ys = allPoints.map((point) => point.y);
  const pad = Math.max(24, style.strokeWidth * 3 + 12);
  const minX = Math.min(...xs) - pad;
  const minY = Math.min(...ys) - pad;
  const maxX = Math.max(...xs) + pad;
  const maxY = Math.max(...ys) + pad;
  const width = Math.max(1, maxX - minX);
  const height = Math.max(1, maxY - minY);
  const toLocal = (point: { x: number; y: number }) => ({ x: point.x - minX, y: point.y - minY });
  const localPoints = points.map(toLocal);
  const localCursor = cursor ? toLocal(cursor) : null;
  const first = localPoints[0] ?? null;
  const last = localPoints[localPoints.length - 1] ?? null;
  const placedPath = localPoints.map((point) => `${point.x},${point.y}`).join(' ');
  const fill = style.fillEnabled ? style.fillColor : 'none';
  const dotRadius = Math.max(3, style.strokeWidth * 0.6 + 1.5);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{
        position: 'absolute',
        left: minX,
        top: minY,
        width,
        height,
        overflow: 'visible',
        pointerEvents: 'none',
        zIndex: 31,
      }}
    >
      {localPoints.length >= 3 && (
        <polygon points={placedPath} fill={fill} fillOpacity={(style.fillOpacity ?? 1) * 0.18} stroke="none" />
      )}
      {localPoints.length >= 2 && (
        <polyline
          points={placedPath}
          fill="none"
          stroke={style.strokeColor}
          strokeOpacity={style.strokeOpacity ?? 1}
          strokeWidth={style.strokeWidth}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}
      {localCursor && last && (
        <line
          x1={last.x}
          y1={last.y}
          x2={localCursor.x}
          y2={localCursor.y}
          stroke={style.strokeColor}
          strokeWidth={style.strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${Math.max(4, style.strokeWidth * 1.5)} ${Math.max(4, style.strokeWidth * 1.5)}`}
          opacity={(style.strokeOpacity ?? 1) * 0.85}
        />
      )}
      {localPoints.map((point, index) => {
        const isFirst = index === 0;
        const highlightFirst = isFirst && draft.canClose && points.length >= 3;
        return (
          <circle
            key={index}
            cx={point.x}
            cy={point.y}
            r={highlightFirst ? dotRadius + 3 : dotRadius}
            fill={highlightFirst ? '#B3D9FF' : '#FFFFFF'}
            stroke={style.strokeColor}
            strokeWidth={Math.max(1, style.strokeWidth * 0.4)}
          />
        );
      })}
      {draft.canClose && points.length >= 3 && first && (
        <circle
          cx={first.x}
          cy={first.y}
          r={dotRadius + 7}
          fill="none"
          stroke="#B3D9FF"
          strokeWidth={1.5}
          opacity={0.9}
        />
      )}
    </svg>
  );
});

PolygonDrawingPreview.displayName = 'PolygonDrawingPreview';

const stopPointerPropagation = (event: React.MouseEvent | React.TouchEvent) => {
  event.stopPropagation();
};

interface CanvasDrawingToolOverlayProps {
  shapeDraft: ShapeDrawingDraft | null;
  pencilDraft: PencilDrawingDraft | null;
  polygonDraft: PolygonDrawingDraft | null;
  textDraft: TextDrawingDraft | null;
  drawingStyle: DrawingToolStyle;
  onTextDraftChange: (value: string) => void;
  onTextBlur: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onTextCancel: () => void;
  onTextCommit: () => void;
}

const CanvasDrawingToolOverlay = React.memo<CanvasDrawingToolOverlayProps>(({
  shapeDraft,
  pencilDraft,
  polygonDraft,
  textDraft,
  drawingStyle,
  onTextDraftChange,
  onTextBlur,
  onTextCancel,
  onTextCommit,
}) => {
  const textInputRef = useRef<HTMLTextAreaElement | null>(null);
  const focusKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!textDraft) {
      focusKeyRef.current = null;
      return;
    }
    const focusKey = [
      textDraft.editingLayerId ?? 'new',
      textDraft.workspaceId,
      textDraft.anchor.x,
      textDraft.anchor.y,
    ].join(':');
    if (focusKeyRef.current !== focusKey) {
      focusKeyRef.current = focusKey;
      textInputRef.current?.focus();
      textInputRef.current?.select();
    }
  }, [textDraft]);

  const normalizedTextStyle = useMemo(
    () => (textDraft ? normalizeDrawingStyle(textDraft.style) : null),
    [textDraft],
  );

  // The `result` objects feed React.memo children: rebuilding them on every parent render (the
  // overlay re-renders on each pointer move) would defeat the memo for the previews that did not
  // change during this drag.
  const shapeResult = useMemo(
    () => (shapeDraft
      ? createShapeDrawingSvg({
        kind: shapeDraft.kind,
        start: shapeDraft.start,
        end: shapeDraft.current,
        style: drawingStyle,
      })
      : null),
    [shapeDraft, drawingStyle],
  );

  const pencilResult = useMemo(
    () => (pencilDraft
      ? createPencilDrawingSvg({ points: pencilDraft.points, style: drawingStyle })
      : null),
    [pencilDraft, drawingStyle],
  );

  const handleTextChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => onTextDraftChange(event.target.value),
    [onTextDraftChange],
  );

  const handleTextKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    event.stopPropagation();
    if (event.key === 'Escape') {
      event.preventDefault();
      onTextCancel();
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onTextCommit();
    }
  }, [onTextCancel, onTextCommit]);

  const textInputStyle = useMemo<React.CSSProperties | null>(() => {
    if (!textDraft) return null;
    const fontSize = normalizedTextStyle?.fontSize ?? drawingStyle.fontSize;
    return {
      position: 'absolute',
      left: textDraft.anchor.x,
      top: textDraft.anchor.y,
      width: Math.max(220, fontSize * 7),
      minHeight: Math.max(48, fontSize * 1.6),
      zIndex: 35,
      resize: 'none',
      borderRadius: '6px',
      border: '1px solid rgba(179,217,255,0.75)',
      backgroundColor: 'rgba(10,10,10,0.84)',
      boxShadow: '0 0 0 1px rgba(0,0,0,0.45), 0 12px 30px rgba(0,0,0,0.42)',
      color: normalizedTextStyle?.fontColor ?? drawingStyle.fontColor,
      fontSize,
      fontWeight: normalizedTextStyle?.fontWeight ?? (drawingStyle.textBold ? 700 : 400),
      fontStyle: (normalizedTextStyle?.textItalic ?? drawingStyle.textItalic) ? 'italic' : 'normal',
      textDecoration: (normalizedTextStyle?.textUnderline ?? drawingStyle.textUnderline) ? 'underline' : 'none',
      lineHeight: normalizedTextStyle?.lineHeight ?? 1.22,
      letterSpacing: `${fontSize * ((normalizedTextStyle?.letterSpacingPercent ?? 0) / 100)}px`,
      textAlign: normalizedTextStyle?.textAlign ?? 'left',
      padding: Math.max(6, Math.round(fontSize * 0.2)),
      outline: 'none',
      fontFamily: normalizedTextStyle?.fontFamily ?? 'ui-sans-serif, -apple-system, system-ui, sans-serif',
    };
  }, [textDraft, normalizedTextStyle, drawingStyle]);

  const overlays: React.ReactNode[] = [];
  if (shapeResult) {
    overlays.push(<DrawingPreviewSvg key="shape" result={shapeResult} />);
  }
  if (pencilResult) {
    overlays.push(<DrawingPreviewSvg key="pencil" result={pencilResult} />);
  }
  if (polygonDraft) {
    overlays.push(
      <PolygonDrawingPreview key="polygon" draft={polygonDraft} drawingStyle={drawingStyle} />
    );
  }

  return (
    <>
      {overlays}
      {textDraft && textInputStyle && (
        <textarea
          ref={textInputRef}
          data-drawing-text-input="true"
          value={textDraft.value}
          onChange={handleTextChange}
          onBlur={onTextBlur}
          onMouseDown={stopPointerPropagation}
          onTouchStart={stopPointerPropagation}
          onKeyDown={handleTextKeyDown}
          style={textInputStyle}
        />
      )}
    </>
  );
});

CanvasDrawingToolOverlay.displayName = 'CanvasDrawingToolOverlay';

export default CanvasDrawingToolOverlay;
